import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {Activity, Calculator as CalcIcon, CheckCircle2, ChevronDown, ChevronUp, Droplets, FileDown, Flame, Gauge, Loader2, Mail, Ruler, Timer, Zap, } from 'lucide-react'
import { lumi } from '@/lib/lumi'
import {
  ACTIVITY_LABELS,
  CM_PER_INCH,
  GOAL_CONFIG,
  LBS_PER_KG,
  calculateShred,
  type ActivityLevel,
  type DeficitGoal,
  type Gender,
  type ShredResults,
  type UnitSystem,
} from '@/lib/shred'
import { generateMeals, type Meal } from '@/lib/plans'
import { exportBlueprint } from '@/lib/pdf'
import MacroChart from '@/components/MacroChart'

const ACTIVITIES: ActivityLevel[] = ['sedentary', 'light', 'moderate', 'active', 'extreme']
const GOALS: DeficitGoal[] = ['lean', 'balanced', 'aggressive']

interface Props {
  onResults: (r: ShredResults, meals: Meal[]) => void
}

// ─── NumericInput ─────────────────────────────────────────────────────────────
// Uses a local string state so the user can freely type, clear, and retype
// without the field snapping back. Commits (clamps + notifies parent) on blur
// or Enter. Re-syncs when `syncKey` changes (unit switch) by re-mounting via key.
// Critically: allows 0 (valid for inches field).
interface NumericInputProps {
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  label: string
  suffix: string
}

function NumericInput({ value, onChange, min, max, label, suffix }: NumericInputProps) {
  // `raw` is what the user sees — can be anything they type mid-edit
  const [raw, setRaw] = useState(() => String(value))
  // Track last successfully committed value for fallback on empty/invalid blur
  const committed = useRef(value)
  const inputRef   = useRef<HTMLInputElement>(null)

  // Commit: parse raw string → clamp → update parent
  const commit = useCallback(() => {
    const stripped = raw.trim().replace(/[^0-9]/g, '')
    const parsed   = stripped === '' ? NaN : parseInt(stripped, 10)
    const next     = Number.isFinite(parsed)
      ? Math.min(max, Math.max(min, parsed))
      : committed.current
    committed.current = next
    setRaw(String(next))
    onChange(next)
  }, [raw, min, max, onChange])

  // Step up/down via arrow buttons — bypass raw string entirely
  const step = useCallback((dir: 1 | -1) => {
    const next = Math.min(max, Math.max(min, committed.current + dir))
    committed.current = next
    setRaw(String(next))
    onChange(next)
    // Return focus to input so keyboard nav keeps working
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [min, max, onChange])

  return (
    <div className="relative flex items-stretch group">
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        aria-label={label}
        value={raw}
        onChange={(e) => {
          // Allow only digits and one leading minus (not needed here, but safe)
          const val = e.target.value.replace(/[^0-9]/g, '')
          setRaw(val)
        }}
        onFocus={(e) => {
          // Select all on focus so user can immediately overtype
          e.target.select()
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
            inputRef.current?.blur()
          }
          if (e.key === 'ArrowUp')   { e.preventDefault(); step(1)  }
          if (e.key === 'ArrowDown') { e.preventDefault(); step(-1) }
        }}
        className="w-full rounded-xl border border-white/[0.09] bg-white/[0.04] py-3.5 pl-4 pr-[72px] font-mono text-[17px] font-semibold text-white caret-blue-400 placeholder-slate-600 transition-colors focus:outline-none focus:border-blue-500/60 focus:bg-white/[0.07]"
      />
      {/* Unit suffix label */}
      <span
        className="pointer-events-none absolute right-[42px] top-1/2 -translate-y-1/2 select-none font-mono text-[11px] font-semibold uppercase tracking-wide text-slate-500"
        aria-hidden="true"
      >
        {suffix}
      </span>
      {/* Stepper buttons */}
      <div className="absolute right-1 top-1 bottom-1 flex w-9 flex-col gap-px overflow-hidden rounded-lg">
        <button
          type="button"
          tabIndex={-1}
          // onMouseDown with preventDefault prevents the input from losing focus
          onMouseDown={(e) => { e.preventDefault(); step(1) }}
          aria-label={`Increase ${label}`}
          className="flex flex-1 items-center justify-center bg-white/[0.05] text-slate-500 transition-colors hover:bg-blue-500/25 hover:text-blue-400 active:bg-blue-500/40"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => { e.preventDefault(); step(-1) }}
          aria-label={`Decrease ${label}`}
          className="flex flex-1 items-center justify-center bg-white/[0.05] text-slate-500 transition-colors hover:bg-blue-500/25 hover:text-blue-400 active:bg-blue-500/40"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

// ─── Segmented Control ────────────────────────────────────────────────────────
function Seg({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div
      role="group"
      className="grid gap-1 rounded-xl border border-white/[0.06] bg-white/[0.03] p-1"
      style={{ gridTemplateColumns: `repeat(${options.length},1fr)` }}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={`rounded-lg py-2.5 text-[13px] font-semibold transition-all ${
            value === o.value
              ? 'bg-blue-600 text-white shadow'
              : 'text-slate-400 hover:bg-white/[0.05] hover:text-white'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ─── Field Label Wrapper ──────────────────────────────────────────────────────
function Field({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
        {icon}
        {label}
      </p>
      {children}
    </div>
  )
}

// ─── Mini Stat (BMR / TDEE / Deficit row) ────────────────────────────────────
function MiniStat({ label, value, unit }: { label: string; value: string; unit: string }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.04] px-3 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 font-mono text-[16px] font-bold leading-tight text-white tabular-nums">
        {value}
        <span className="ml-1 text-[10px] text-slate-500">{unit}</span>
      </div>
    </div>
  )
}

// ─── Insight Card (FFMI / Water / Timeline) ───────────────────────────────────
function InsightCard({
  icon,
  label,
  value,
  sub,
  accent,
}: {
  icon: React.ReactNode
  label: string
  value: string
  sub: string
  accent: string
}) {
  return (
    <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-5">
      <div className={accent}>{icon}</div>
      <div className="mt-3 text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 font-mono text-[22px] font-bold leading-none text-white tabular-nums">{value}</div>
      <div className="mt-1.5 text-[12px] leading-snug text-slate-500">{sub}</div>
    </div>
  )
}

// ─── Body-fat category label ───────────────────────────────────────────────
function bfLabel(pct: number): string {
  if (pct <= 6)  return 'Essential Fat'
  if (pct <= 13) return 'Athletic'
  if (pct <= 17) return 'Fitness'
  if (pct <= 24) return 'Average'
  return 'Above Average'
}

// ─── Main Calculator ──────────────────────────────────────────────────────────
export default function Calculator({ onResults }: Props) {

  // ── Form state ──────────────────────────────────────────────────────────────
  const [gender,     setGender]     = useState<Gender>('male')
  const [units,      setUnitsState] = useState<UnitSystem>('imperial')
  const [age,        setAge]        = useState(28)

  // Imperial display values
  const [lbs,    setLbs]    = useState(185)
  const [feet,   setFeet]   = useState(5)
  const [inches, setInches] = useState(11)

  // Metric display values
  const [kg,       setKg]       = useState(84)
  const [cmHeight, setCmHeight] = useState(180)

  // Body fat
  const [useBodyFat, setUseBodyFat] = useState(false)
  const [bodyFat,    setBodyFat]    = useState(18)

  // Activity + goal
  const [activity, setActivity] = useState<ActivityLevel>('moderate')
  const [goal,     setGoal]     = useState<DeficitGoal>('balanced')

  // syncKey — bumped on unit switch to force NumericInput remount + re-init
  const [syncKey, setSyncKey] = useState(0)
  const [email, setEmail] = useState('')
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [emailMessage, setEmailMessage] = useState('')

  // ── Unit switch ─────────────────────────────────────────────────────────────
  // Convert displayed values to the other unit system before switching
  const switchUnits = useCallback((next: UnitSystem) => {
    if (next === units) return
    if (next === 'metric') {
      // imperial → metric
      const newKg = Math.round(lbs / LBS_PER_KG)
      const newCm = Math.round((feet * 12 + inches) * CM_PER_INCH)
      setKg(newKg)
      setCmHeight(newCm)
    } else {
      // metric → imperial
      const newLbs   = Math.round(kg * LBS_PER_KG)
      const totalIn  = Math.round(cmHeight / CM_PER_INCH)
      setLbs(newLbs)
      setFeet(Math.floor(totalIn / 12))
      setInches(totalIn % 12)
    }
    setUnitsState(next)
    setSyncKey((k) => k + 1)  // force remount of all NumericInputs
  }, [units, lbs, feet, inches, kg, cmHeight])

  // ── Normalise to internal units for the engine ───────────────────────────────
  const weightLbs = units === 'imperial' ? lbs          : kg * LBS_PER_KG
  const heightCm  = units === 'imperial' ? (feet * 12 + inches) * CM_PER_INCH : cmHeight

  // ── Run calculations (recomputes on any input change) ────────────────────────
  const results: ShredResults = useMemo(
    () => calculateShred({ gender, units, weightLbs, heightCm, age, useBodyFat, bodyFat, activity, goal }),
    [gender, units, weightLbs, heightCm, age, useBodyFat, bodyFat, activity, goal],
  )

  const meals = useMemo(() => generateMeals(results), [results])

  // ── Push results up without causing an infinite re-render loop ───────────────
  // Strategy: keep a ref to the latest `onResults` callback (always fresh),
  // and only call it when the computed numbers actually change (string-keyed).
  const onResultsRef = useRef(onResults)
  useEffect(() => { onResultsRef.current = onResults })

  const prevResultKey = useRef<string>('')
  useEffect(() => {
    const key = [
      results.bmr, results.tdee, results.deficitPct, results.dailyCalories,
      results.protein, results.fats, results.carbs,
      results.ffmi ?? 'x', results.waterLiters, results.weeksToLean,
      results.formula, results.leanBodyMassLbs ?? 'x',
    ].join('|')
    if (key === prevResultKey.current) return
    prevResultKey.current = key
    onResultsRef.current(results, meals)
  }, [results, meals])

  const sendBlueprintEmail = async () => {
    const trimmed = email.trim()
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
    if (!isValid) {
      setEmailStatus('error')
      setEmailMessage('Enter a valid email address.')
      return
    }
    setEmailStatus('sending')
    setEmailMessage('')
    try {
      await lumi.functions.invoke('sendBlueprint', {
        method: 'POST',
        body: { email: trimmed, results, meals },
      })
      setEmailStatus('sent')
      setEmailMessage(`Blueprint sent to ${trimmed}. Check inbox and spam.`)
    } catch (error: unknown) {
      const err = error as { data?: { error?: string } }
      setEmailStatus('error')
      setEmailMessage(err?.data?.error || 'Unable to send the blueprint. Try again.')
    }
  }

  // ── JSX ─────────────────────────────────────────────────────────────────────
  return (
    <section id="calculator" className="relative py-20 scroll-mt-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">

        {/* Section header */}
        <header className="max-w-2xl">
          <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-blue-500">
            The Engine
          </span>
          <h2 className="font-display mt-2 text-[32px] font-bold tracking-tight text-white sm:text-[40px]">
            Shred Calculator — Build Your Plan
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-slate-400">
            Enter your real stats to receive a science-backed{' '}
            <strong className="text-slate-300">lean and ripped body</strong> blueprint.
            Toggle Body Fat % to unlock <strong className="text-slate-300">Katch-McArdle</strong>,
            FFMI scoring, and a precise shred timeline.
          </p>
        </header>

        <div className="mt-10 grid items-start gap-6 lg:grid-cols-[1fr_1.1fr] xl:gap-8">

          {/* ══════════════════════ FORM ══════════════════════ */}
          <form
            className="space-y-6 rounded-3xl border border-white/[0.07] bg-white/[0.015] p-6 sm:p-8"
            onSubmit={(e) => e.preventDefault()}
            aria-label="Shred Calculator inputs"
            noValidate
          >

            {/* Row: Gender + Units */}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Gender" icon={<Activity className="h-3.5 w-3.5" />}>
                <Seg
                  options={[
                    { value: 'male',   label: 'Male'   },
                    { value: 'female', label: 'Female' },
                  ]}
                  value={gender}
                  onChange={(v) => setGender(v as Gender)}
                />
              </Field>
              <Field label="Units" icon={<Ruler className="h-3.5 w-3.5" />}>
                <Seg
                  options={[
                    { value: 'imperial', label: 'LBS/FT' },
                    { value: 'metric',   label: 'KG/CM'  },
                  ]}
                  value={units}
                  onChange={(v) => switchUnits(v as UnitSystem)}
                />
              </Field>
            </div>

            {/* Row: Weight + Age */}
            <div className="grid grid-cols-2 gap-4">
              <Field label={units === 'imperial' ? 'Weight — lbs' : 'Weight — kg'}>
                {units === 'imperial' ? (
                  <NumericInput
                    key={`w-imp-${syncKey}`}
                    value={lbs}
                    onChange={setLbs}
                    min={60}
                    max={700}
                    label="Weight in lbs"
                    suffix="lbs"
                  />
                ) : (
                  <NumericInput
                    key={`w-met-${syncKey}`}
                    value={kg}
                    onChange={setKg}
                    min={30}
                    max={300}
                    label="Weight in kg"
                    suffix="kg"
                  />
                )}
              </Field>
              <Field label="Age — years">
                <NumericInput
                  key={`age-${syncKey}`}
                  value={age}
                  onChange={setAge}
                  min={14}
                  max={100}
                  label="Age in years"
                  suffix="yrs"
                />
              </Field>
            </div>

            {/* Height */}
            <Field label={units === 'imperial' ? 'Height — ft & in' : 'Height — cm'}>
              {units === 'imperial' ? (
                <div className="grid grid-cols-2 gap-3">
                  <NumericInput
                    key={`ft-${syncKey}`}
                    value={feet}
                    onChange={setFeet}
                    min={3}
                    max={8}
                    label="Feet"
                    suffix="ft"
                  />
                  <NumericInput
                    key={`in-${syncKey}`}
                    value={inches}
                    onChange={setInches}
                    min={0}
                    max={11}
                    label="Inches"
                    suffix="in"
                  />
                </div>
              ) : (
                <NumericInput
                  key={`cm-${syncKey}`}
                  value={cmHeight}
                  onChange={setCmHeight}
                  min={100}
                  max={280}
                  label="Height in centimeters"
                  suffix="cm"
                />
              )}
            </Field>

            {/* Body Fat Toggle + Slider */}
            <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[14px] font-semibold leading-tight text-white">Body Fat %</p>
                  <p className="mt-0.5 text-[12px] text-slate-500">
                    Unlocks Katch-McArdle + FFMI scoring
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={useBodyFat}
                  onClick={() => setUseBodyFat((v) => !v)}
                  className={`relative h-7 w-14 flex-shrink-0 rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    useBodyFat ? 'bg-blue-600' : 'bg-slate-700'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 h-5 w-5 rounded-full bg-white shadow-md transition-transform duration-200 ${
                      useBodyFat ? 'translate-x-7' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {useBodyFat && (
                <div className="mt-4 space-y-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[12px] text-slate-400">Estimated body fat</span>
                    <span className="font-mono text-[22px] font-bold tabular-nums text-blue-400">
                      {bodyFat}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min={4}
                    max={45}
                    step={1}
                    value={bodyFat}
                    onChange={(e) => setBodyFat(Number(e.target.value))}
                    className="w-full"
                    aria-label="Body fat percentage slider"
                  />
                  <div className="flex justify-between font-mono text-[10px] text-slate-600">
                    <span>4%</span>
                    <span>15%</span>
                    <span>25%</span>
                    <span>35%</span>
                    <span>45%</span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1">
                    <span className="text-[11px] text-slate-400">Category:</span>
                    <span className="text-[11px] font-semibold text-blue-400">{bfLabel(bodyFat)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Activity Level */}
            <Field label="Activity Level" icon={<Gauge className="h-3.5 w-3.5" />}>
              <div className="space-y-1.5">
                {ACTIVITIES.map((a) => (
                  <button
                    key={a}
                    type="button"
                    aria-pressed={activity === a}
                    onClick={() => setActivity(a)}
                    className={`w-full rounded-xl border px-4 py-2.5 text-left transition-all ${
                      activity === a
                        ? 'border-blue-500/50 bg-blue-500/[0.08]'
                        : 'border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.02]'
                    }`}
                  >
                    <span className={`text-[13px] font-semibold ${activity === a ? 'text-white' : 'text-slate-300'}`}>
                      {ACTIVITY_LABELS[a].label}
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">
                      {ACTIVITY_LABELS[a].desc}
                    </span>
                  </button>
                ))}
              </div>
            </Field>

            {/* Shred Intensity / Deficit Goal */}
            <Field label="Shred Intensity" icon={<Zap className="h-3.5 w-3.5" />}>
              <div className="grid grid-cols-3 gap-2">
                {GOALS.map((g) => (
                  <button
                    key={g}
                    type="button"
                    aria-pressed={goal === g}
                    onClick={() => setGoal(g)}
                    className={`rounded-xl border px-2 py-3.5 text-center transition-all ${
                      goal === g
                        ? 'border-blue-500/50 bg-blue-500/[0.08]'
                        : 'border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.02]'
                    }`}
                  >
                    <span className={`block text-[12px] font-semibold leading-tight ${goal === g ? 'text-white' : 'text-slate-300'}`}>
                      {GOAL_CONFIG[g].label}
                    </span>
                    <span className="mt-1 block font-mono text-[14px] font-bold text-blue-400">
                      −{GOAL_CONFIG[g].pct * 100}%
                    </span>
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                {GOAL_CONFIG[goal].desc}
              </p>
            </Field>

          </form>

          {/* ══════════════════════ RESULTS PANEL ══════════════════════ */}
          <div className="space-y-5">

            {/* ── Hero calorie card ── */}
            <div className="relative overflow-hidden rounded-3xl border border-blue-500/25 bg-gradient-to-br from-blue-600/[0.14] via-slate-900/60 to-transparent p-7 sm:p-8">
              {/* Glow orb */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full opacity-30 blur-[70px]"
                style={{ background: 'radial-gradient(circle,#3b82f6,transparent 70%)' }}
              />

              <div className="relative">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-blue-400">
                  <Flame className="h-4 w-4" aria-hidden="true" />
                  Daily Calorie Target
                </div>
                <div className="mt-2 font-mono text-[58px] font-bold leading-none text-white tabular-nums sm:text-[70px]">
                  {results.dailyCalories.toLocaleString()}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-slate-400">
                  <span>kcal / day</span>
                  <span aria-hidden="true" className="inline-block h-1 w-1 rounded-full bg-slate-600" />
                  <span>{results.deficitPct}% caloric deficit</span>
                  <span aria-hidden="true" className="inline-block h-1 w-1 rounded-full bg-slate-600" />
                  <span className="font-mono text-[11px] text-blue-400">{results.formula}</span>
                </div>
              </div>

              {/* BMR / TDEE / Deficit stats */}
              <div className="relative mt-6 grid grid-cols-3 gap-2.5">
                <MiniStat label="BMR"     value={results.bmr.toLocaleString()}  unit="kcal" />
                <MiniStat label="TDEE"    value={results.tdee.toLocaleString()} unit="kcal" />
                <MiniStat label="Deficit" value={`−${results.deficitPct}`}      unit="%"    />
              </div>

              {/* Lean Body Mass row — only when BF% is enabled */}
              {results.leanBodyMassLbs !== null && (
                <div className="relative mt-3 flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-2.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Lean Body Mass
                  </span>
                  <span className="font-mono text-[15px] font-bold tabular-nums text-white">
                    {results.leanBodyMassLbs} lbs
                    <span className="ml-2 text-[11px] text-slate-500">
                      ({Math.round(results.leanBodyMassLbs / LBS_PER_KG)} kg)
                    </span>
                  </span>
                </div>
              )}
            </div>

            {/* ── Macronutrient chart ── */}
            <div className="rounded-3xl border border-white/[0.07] bg-white/[0.015] p-6 sm:p-7">
              <h3 className="font-display mb-5 flex items-center gap-2 text-[17px] font-semibold text-white">
                <CalcIcon className="h-5 w-5 text-blue-500" aria-hidden="true" />
                Dynamic Macronutrient Split
              </h3>
              <MacroChart
                protein={results.protein}
                proteinCals={results.proteinCals}
                carbs={results.carbs}
                carbsCals={results.carbsCals}
                fats={results.fats}
                fatsCals={results.fatsCals}
              />
            </div>

            {/* ── Insight cards: FFMI / Water / Timeline ── */}
            <div className="grid gap-3 sm:grid-cols-3">
              <InsightCard
                icon={<Gauge className="h-5 w-5" aria-hidden="true" />}
                label="FFMI"
                value={results.ffmi !== null ? String(results.ffmi) : '—'}
                sub={results.ffmiClass ?? 'Enable body fat % to unlock'}
                accent="text-blue-400"
              />
              <InsightCard
                icon={<Droplets className="h-5 w-5" aria-hidden="true" />}
                label="Daily Water"
                value={`${results.waterLiters}L`}
                sub={`${results.waterCups} US cups / day`}
                accent="text-cyan-400"
              />
              <InsightCard
                icon={<Timer className="h-5 w-5" aria-hidden="true" />}
                label="Shred Timeline"
                value={results.weeksToLean > 0 ? `${results.weeksToLean}w` : '—'}
                sub={
                  results.weeksToLean > 0
                    ? `≈ ${Math.round(results.weeksToLean / 4.33)} months to ${results.targetBodyFat}% BF`
                    : `Target: ${results.targetBodyFat}% body fat`
                }
                accent="text-indigo-400"
              />
            </div>

            {/* ── PDF Export + email delivery ── */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => exportBlueprint(results, meals)}
                className="inline-flex w-full items-center justify-center gap-2.5 rounded-2xl bg-white px-6 py-4 text-[14px] font-bold tracking-wide text-slate-900 shadow-md shadow-black/20 transition-all duration-200 hover:bg-blue-500 hover:text-white"
              >
                <FileDown className="h-5 w-5" aria-hidden="true" />
                Download My Shred Blueprint (PDF)
              </button>

              <form
                className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4 sm:p-5"
                onSubmit={async (e) => {
                  e.preventDefault()
                  await sendBlueprintEmail()
                }}
              >
                <label htmlFor="blueprint-email" className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                  <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                  Email my blueprint
                </label>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <input
                    id="blueprint-email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      if (emailStatus !== 'idle') {
                        setEmailStatus('idle')
                        setEmailMessage('')
                      }
                    }}
                    className="w-full rounded-xl border border-white/[0.09] bg-white/[0.04] px-4 py-3 text-[14px] text-white placeholder-slate-600 transition-colors focus:outline-none focus:border-blue-500/60 focus:bg-white/[0.07]"
                  />
                  <button
                    type="submit"
                    disabled={emailStatus === 'sending'}
                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-[13px] font-bold text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {emailStatus === 'sending' ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : emailStatus === 'sent' ? (
                      <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Mail className="h-4 w-4" aria-hidden="true" />
                    )}
                    {emailStatus === 'sending' ? 'Sending…' : emailStatus === 'sent' ? 'Sent' : 'Send'}
                  </button>
                </div>
                {emailMessage ? (
                  <p
                    role={emailStatus === 'error' ? 'alert' : 'status'}
                    className={`mt-2 text-[12px] ${emailStatus === 'error' ? 'text-rose-400' : 'text-emerald-400'}`}
                  >
                    {emailMessage}
                  </p>
                ) : (
                  <p className="mt-2 text-[12px] text-slate-500">
                    We email your calories, macros, hydration, and meal blueprint.
                  </p>
                )}
              </form>
            </div>

          </div>
        </div>
      </div>
    </section>
  )
}
